import type { DriverHosClocks, MembershipHosClocks } from "../web-client/api/data-contracts";
import * as operations from "../web-client/api/operations";
import type { Transport } from "../web-client/transport";

/**
 * One driver's hours of service, or the fact that they have none.
 *
 * The generated `MembershipHosClocks` types `hos` as always present. That is the OpenAPI 3.0 generator dropping the
 * nullability of a `$ref`, not the API's contract — the same thing it does to `ResolvedRoute.forecast` — and the API
 * really does answer `hos: null` for a driver whose ELD holds no timer, and for every driver of a workspace whose ELD
 * reports no HOS. Restated here so a caller branches on it rather than reading fields off undefined.
 */
export type MembershipHosClocksResult = Omit<MembershipHosClocks, "hos"> & { hos: DriverHosClocks | null };

/** Query keys for the HOS reads, so a poll and a one-off read of the same thing share a cache entry. */
export const hosKeys = {
  workspaceClocks: (workspaceId: string) => ["hos", "clocks", workspaceId] as const,
  membershipClocks: (workspaceId: string, membershipId: string) =>
    ["hos", "clocks", workspaceId, membershipId] as const,
};

/**
 * Every driver in the workspace whose ELD holds an HOS timer, and what their clocks say.
 */
export async function getWorkspaceHosClocks(
  transport: Transport,
  { workspaceId }: { workspaceId: string },
): Promise<{ drivers: MembershipHosClocksResult[] }> {
  return operations.getWorkspacesHosClocks(transport, { workspaceId });
}

/**
 * One driver's hours of service, in the same element shape as the workspace read; `hos` is null for a driver with no
 * timer.
 */
export async function getMembershipHosClocks(
  transport: Transport,
  { workspaceId, membershipId }: { workspaceId: string; membershipId: string },
): Promise<MembershipHosClocksResult> {
  return operations.getWorkspacesMembershipsHosClocks(transport, { workspaceId, membershipId });
}
